
<template>
    <div v-if="'completed_by_partner' === localMethod">
        <FormGeolocationCompletedByPartner
            :index="props.index"
            :number="props.number"
            :reference="props.reference"
            :optionsCountry="props.optionsCountry"
        />
    </div>
    <div v-else>
        <FormKit
            name="manualMethod"
            v-model="localMethod"
            type="radio"
            step="any"
            label="Méthode"
            :options="{
                address: 'Adresse',
                geocoding: 'Latitude/Longitude'                    
            }"
        />
        <div v-if="warningRequestGeolocation" class="status-geolocation">
            <small class="warning-geolocation">{{ warningRequestGeolocation }}</small>
        </div>
        <div v-if="errorRequestGeolocation" class="status-geolocation">
            <small class="error-geolocation">{{ errorRequestGeolocation }}</small>
        </div>
        <FormKit
            v-show="'address' === localMethod"
            v-model="localAddress"
            type="text"
            step="any"
            :label="props.number + '. Adresse*'"
            name="address"
            placeholder="Adresse"
            @keyup="inputAddress"
        />
        <FormKit
            v-show="'address' === localMethod"
            v-model="localZipCode"
            type="text"
            step="any"
            :label="props.number + '. Code Postal*'"
            name="zipCode"
            placeholder="Code Postal"
            @keyup="inputAddress"
        />
        <FormKit
            v-show="'address' === localMethod"
            v-model="localCity"
            type="text"
            step="any"
            :label="props.number + '. Ville*'"
            name="city"
            placeholder="Ville"
            @keyup="inputAddress"
        />
        <FormKit
            v-show="'address' === localMethod"
            v-model="localCountry"
            type="select"
            :label="props.number + '. Pays*'"
            name="country"
            placeholder="Pays"
            :options="props.optionsCountry"
            @change="handleCountryChange"
        />
        <FormKit
            v-show="'geocoding' === localMethod"
            v-model="localFormattedAddress"
            disabled
            type="textarea"
            step="any"
            rows="2"
            :label="props.number + '. Adresse correspondant aux coordonnées*'"
            name="formattedAddress"
            placeholder="Adresse complète"
            validation="required"
            :validation-messages="validationMessages"
            validation-visibility="submit"
        />
        <div v-if="'address' === localMethod && addressFound" class="suggestion-formatted-address">
            <label class="formkit-label">Coordonnées pour l'adresse : </label>
            <div>
                <small>{{ addressFound }}</small>
            </div>
            <div class="suggestion-formatted-address-alert">
                <small>{{ `${t('ProductForm.Geolocation.alertVerificationAddress')}` }}</small>
            </div>
        </div>
        <FormKit
            :disabled="'address' === localMethod"
            v-model="localLatitude"
            type="number"
            step="any"
            :label="props.number + '. Latitude*'"
            name="latitude"
            placeholder="Latitude"
            validation="required|min:-90|max:90|maximumDecimalsGeocoding"
            :validation-rules="{ maximumDecimalsGeocoding }"
            :validation-messages="validationMessages"
            validation-visibility="submit"
            @keyup="inputGeocode"
        />
        <FormKit
            :disabled="'address' === localMethod"
            v-model="localLongitude"
            type="number"
            step="any"
            :label="props.number + '. Longitude*'"
            name="longitude"
            placeholder="Longititude"
            validation="required|min:-90|max:90|maximumDecimalsGeocoding"
            :validation-rules="{ maximumDecimalsGeocoding }"
            :validation-messages="validationMessages"
            validation-visibility="submit"
            @keyup="inputGeocode"
        />
        <FormKit
            :disabled="requestingGeolocation || (!activateRequestGeolocation && !props.isEditMode)"
            v-if="'geocoding' !== localMethod"
            type="button"
            :label="requestingGeolocation ? `${t('ProductForm.Geolocation.actions.requestingGeolocation')}` : `${t('ProductForm.Geolocation.actions.getGeocode')}`"
            :class="{ 'btn-secondary': true, 'btn-block': true }"
            @click="getGeocodeByAddress"
        />
        <FormKit
            :disabled="requestingGeolocation || (!activateRequestGeolocation && !props.isEditMode)"
            v-if="'geocoding' === localMethod"
            type="button"
            :label="requestingGeolocation ? `${t('ProductForm.Geolocation.actions.requestingGeolocation')}` : `${t('ProductForm.Geolocation.actions.getAddress')}`"
            :class="{ 'btn-secondary': true, 'btn-block': true }"
            @click="getAddressByGeocode"
        />
        <div v-if="props.isEditMode" class="edit-mode-notice">
            <small>{{ `${t('ProductForm.Geolocation.editModeNotice')}` }}</small>
        </div>
    </div>
</template>


<script setup lang="ts">
import { ref, watch, onMounted, defineEmits, computed } from "vue";
import ApiService from "@/_services/api.service";
import { maximumDecimalsGeocoding } from "@/_services/geolocation/validations";
import { useI18n } from "vue-i18n";
import FormGeolocationCompletedByPartner from "@/components/forms/product/FormGeolocationCompletedByPartner.vue";

const { t } = useI18n();

// Define types for better type safety
interface GeolocationReference {
    manualMethod: string | null;
    address: string | null;
    zipCode: string | null;
    city: string | null;
    country: string | null;
    latitude: number | null;
    longitude: number | null;
    formattedAddress: string | null;
    status?: string | null;
}

interface GeolocationEmitData extends GeolocationReference {
    index: number | undefined;
}

interface CountryMapping {
    [key: string]: string;
}

const validationMessages = {
    maximumDecimalsGeocoding: `${t('ProductForm.Geolocation.validationMessage.maximumDecimalsGeocoding')}`,
    required: `${t('ProductForm.Geolocation.validationMessage.required')}`,
    min: `${t('ProductForm.Geolocation.validationMessage.min')}`,
    max: `${t('ProductForm.Geolocation.validationMessage.max')}`
};

// Define Props
const props = defineProps({
    'index': {
        type: Number
    },
    'number': {
        type: Number
    },
    'reference': {
        type: Object as () => GeolocationReference,
        default: () => ({
            manualMethod: null,
            address: null,
            zipCode: null,
            city: null,
            country: null,
            latitude: null,
            longitude: null,
            formattedAddress: null
        })
    },
    'optionsCountry': {
        type: Object
    },
    'isEditMode': {
        type: Boolean,
        default: false
    }
});

// Define emits to communicate changes to parent
const emit = defineEmits<{
    (e: 'update:geolocation', data: GeolocationEmitData): void
}>();

// Define local state variables with proper initialization from props
const localMethod = ref<string>(props.reference.manualMethod || 'address');
const localAddress = ref<string | null>(props.reference.address || null);
const localZipCode = ref<string | null>(props.reference.zipCode || null);
const localCity = ref<string | null>(props.reference.city || null);
const localCountry = ref<string | null>(null); // Will be set properly after ensureCountryCode is defined
const localLatitude = ref<number | null>(props.reference.latitude || null);
const localLongitude = ref<number | null>(props.reference.longitude || null);
const localFormattedAddress = ref<string | null>(props.reference.formattedAddress || null);

// Define refs for UI state
const initialStatus = ref<string | null>(props.reference.status ?? null);
const activateRequestGeolocation = ref<boolean>(false);
const requestingGeolocation = ref<boolean>(false);
const warningRequestGeolocation = ref<string | null>(null);
const errorRequestGeolocation = ref<string | null>(null);
const addressFound = ref<string | null>(null);

// Function to ensure country is always a code, not a full name
const ensureCountryCode = (country: string | null): string | null => {
    if (!country) return null;

    // Mapping of country names to codes
    const countryMapping: CountryMapping = {
        'France': 'FR',
        'Belgique': 'BE',
        'Suisse': 'CH',
        'Luxembourg': 'LU',
        'Allemagne': 'DE',
        'Italie': 'IT',
        'Espagne': 'ES'
    };

    // If country is a full name, convert it to code
    return countryMapping[country] || country;
};

// Now that ensureCountryCode is defined, we can set localCountry
localCountry.value = ensureCountryCode(props.reference.country);

// Define helper functions
const isEmpty = (val: any): boolean => {
    return (val === undefined || val === null || val === '');
};

const updateStatusMessages = (): void => {
    if ('geocode_not_found_by_address' === initialStatus.value) {
        errorRequestGeolocation.value = `${t('ProductForm.Geolocation.zeroResultsGetGeocodeByAddress')}`;
    } else if ('address_not_found_by_geocode' === initialStatus.value) {
        errorRequestGeolocation.value = `${t('ProductForm.Geolocation.zeroResultsGetAddressByGeocode')}`;
    }
};

const resetMessages = (): void => {
    warningRequestGeolocation.value = null;
    errorRequestGeolocation.value = null;
    addressFound.value = null;
};

const canRequestGeocodeByAddress = (): boolean => {
    return !isEmpty(localAddress.value) &&
        !isEmpty(localZipCode.value) &&
        !isEmpty(localCity.value) &&
        !isEmpty(localCountry.value);
};

const canRequestAddressByGeocode = (): boolean => {
    return !isEmpty(localLatitude.value) && !isEmpty(localLongitude.value);
};

// Handler for country change event
const handleCountryChange = (event: any): void => {
    // Ensure country is a code, not a full name
    localCountry.value = ensureCountryCode(event.target ? event.target.value : event);
    inputAddress();
};

// Function to emit changes to parent
const emitChanges = (): void => {
    emit('update:geolocation', {
        manualMethod: localMethod.value,
        address: localAddress.value,
        zipCode: localZipCode.value,
        city: localCity.value,
        country: localCountry.value,
        latitude: localLatitude.value,
        longitude: localLongitude.value,
        formattedAddress: localFormattedAddress.value,
        index: props.index
    });
};

const inputAddress = (): void => {
    resetMessages();

    if ('address' === localMethod.value) {
        localLatitude.value = null;
        localLongitude.value = null;
        localFormattedAddress.value = null;

        // Only activate request if all required fields are filled
        activateRequestGeolocation.value = canRequestGeocodeByAddress();
    }

    emitChanges();
};

const inputGeocode = (): void => {
    resetMessages();

    if ('geocoding' === localMethod.value) {
        localAddress.value = null;
        localZipCode.value = null;
        localCity.value = null;
        localCountry.value = null;
        localFormattedAddress.value = null;

        // Only activate request if all required fields are filled
        activateRequestGeolocation.value = canRequestAddressByGeocode();
    }

    emitChanges();
};

const beforeRequestGeolocation = (): void => {
    resetMessages();
    requestingGeolocation.value = true;
};

const getAddressByGeocode = async (): Promise<void> => {
    beforeRequestGeolocation();

    // Validate inputs before making the request
    if (!canRequestAddressByGeocode()) {
        errorRequestGeolocation.value = `${t('ProductForm.Geolocation.errorMissingCoordinates')}`;
        requestingGeolocation.value = false;
        return;
    }

    // Request
    const payload = {
        'latitude': parseFloat(String(localLatitude.value)),
        'longitude': parseFloat(String(localLongitude.value)),
        'isEditMode': props.isEditMode
    };

    try {
        const response = await ApiService.post("geolocation/reverse-geocoding", payload);
        const responseData = response.data.data;

        if ('OK' === responseData.status) {
            localAddress.value = responseData.address;
            localZipCode.value = responseData.zipCode;
            localCity.value = responseData.city;
            localCountry.value = responseData.countryCode;
            localFormattedAddress.value = responseData.formattedAddress;

            if (null === responseData.address || null === responseData.zipCode || null === responseData.city) {
                warningRequestGeolocation.value = `${t('ProductForm.Geolocation.noCompleteResultGetAddressByGeocode')}`;
            }

            activateRequestGeolocation.value = false;
            emitChanges();
        } else if ('ZERO_RESULTS' === responseData.status) {
            errorRequestGeolocation.value = `${t('ProductForm.Geolocation.zeroResultsGetAddressByGeocode')}`;
            activateRequestGeolocation.value = false;
        } else {
            errorRequestGeolocation.value = `${t('ProductForm.Geolocation.errorGetAddressByGeocode')}`;
        }
    } catch (error) {
        console.error('Error in reverse geocoding:', error);
        errorRequestGeolocation.value = `${t('ProductForm.Geolocation.errorGetAddressByGeocode')}`;
    }

    requestingGeolocation.value = false;
};

const getGeocodeByAddress = async (): Promise<void> => {
    beforeRequestGeolocation();

    // Validate inputs before making the request
    if (!canRequestGeocodeByAddress()) {
        errorRequestGeolocation.value = `${t('ProductForm.Geolocation.errorMissingAddressFields')}`;
        requestingGeolocation.value = false;
        return;
    }

    // Ensure country is a code, not a full name
    const countryCode = ensureCountryCode(localCountry.value);

    // Request
    const payload = {
        'address': localAddress.value,
        'zipCode': localZipCode.value,
        'city': localCity.value,
        'countryCode': countryCode,
        'isEditMode': props.isEditMode
    };

    try {
        const response = await ApiService.post("geolocation/geocoding", payload);
        const geocode = response.data.data;

        if ('OK' === geocode.status) {
            localLatitude.value = geocode.latitude;
            localLongitude.value = geocode.longitude;
            localFormattedAddress.value = geocode.formattedAddress;

            addressFound.value = geocode.formattedAddress;
            activateRequestGeolocation.value = false;
            emitChanges();
        } else if ('ZERO_RESULTS' === geocode.status) {
            errorRequestGeolocation.value = `${t('ProductForm.Geolocation.zeroResultsGetGeocodeByAddress')}`;
            activateRequestGeolocation.value = false;
        } else {
            errorRequestGeolocation.value = `${t('ProductForm.Geolocation.errorGetGeocodeByAddress')}`;
        }
    } catch (error) {
        console.error('Error in geocoding:', error);
        errorRequestGeolocation.value = `${t('ProductForm.Geolocation.errorGetGeocodeByAddress')}`;
    }

    requestingGeolocation.value = false;
};

// Initialize component
onMounted(() => {
    // Ensure country code is correct in the reference data
    if (props.reference && props.reference.country) {
        localCountry.value = ensureCountryCode(props.reference.country);
    }

    // Set initial status messages
    updateStatusMessages();

    // In edit mode, we should have all the data already
    if (props.isEditMode) {
        activateRequestGeolocation.value = true;
    }
});

// Watch for changes in the reference prop to update local state
watch(() => props.reference, (newReference) => {
    if (newReference) {
        localMethod.value = newReference.manualMethod || 'address';
        localAddress.value = newReference.address || null;
        localZipCode.value = newReference.zipCode || null;
        localCity.value = newReference.city || null;
        localCountry.value = ensureCountryCode(newReference.country);
        localLatitude.value = newReference.latitude || null;
        localLongitude.value = newReference.longitude || null;
        localFormattedAddress.value = newReference.formattedAddress || null;
        initialStatus.value = newReference.status ?? null;

        // Set initial status messages
        updateStatusMessages();
    }
}, { deep: true });

// Define watcher for method changes
watch(localMethod, (value) => {
    resetMessages();

    if ('address' === value) {
        // Keep existing address data in edit mode
        if (!props.isEditMode) {
            localLatitude.value = null;
            localLongitude.value = null;
            localFormattedAddress.value = null;
        }
        activateRequestGeolocation.value = canRequestGeocodeByAddress();
    } else if ('geocoding' === value) {
        // Keep existing geocoding data in edit mode
        if (!props.isEditMode) {
            localAddress.value = null;
            localZipCode.value = null;
            localCity.value = null;
            localCountry.value = null;
            localFormattedAddress.value = null;
        }
        activateRequestGeolocation.value = canRequestAddressByGeocode();
    }

    emitChanges();
});
</script>

<style scoped lang="scss">
.status-geolocation {
    margin-bottom: 10px;
}

.warning-geolocation {
    color: #ffb84d;
}

.error-geolocation {
    color: #ea0000;
}

.suggestion-formatted-address {
    margin-bottom: 15px;
}

.suggestion-formatted-address-alert {
    margin-top: 5px;
}

.edit-mode-notice {
    margin-top: 10px;
    margin-bottom: 10px;
    font-style: italic;
    color: #666;
}
</style>
